import React, { useEffect, useMemo, useState, useContext } from 'react';

// Import Components
import LoadingSpinner from '../../Elements/LoadingSpinner';

// Context / Services
import { UserContext } from '../../../context/User';
import paymentService from '../../../services/paymentService';
import subService from '../../../services/subscriptionService';

/**
 * Props:
 * - setNewCard: (bool -> void) toggles "new card" entry flow
 * - scriptId: number | string
 * - invoice: { id?: string } | null
 * - nextStep: (stepNumber: number) => void
 * - orderInfo: { selectedPlan?: { details?: any }, ... }
 * - cards?: array of saved payment methods (optional; falls back to user.cards)
 * - defaultPaymentMethodId?: string (optional; if provided will preselect)
 */
const CheckoutForm = ({
  setNewCard,
  scriptId,
  invoice,
  nextStep,
  orderInfo,
  cards,
  defaultPaymentMethodId,
  userId,
}) => {
  const { user } = useContext(UserContext);
  const targetUserId = userId ?? user?.id;

  const availableCards = useMemo(() => {
    if (Array.isArray(cards)) return cards;
    return Array.isArray(user?.cards) ? user.cards : [];
  }, [cards, user?.cards]);

  const [selectedCardId, setSelectedCardId] = useState(null);
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // --- Brand Badge (fixed width to align radio bullets consistently) ---
  const BrandBadge = ({ brand }) => {
    const labelRaw = (brand || 'CARD').toUpperCase();
    const label = labelRaw.includes('MASTERCARD')
      ? 'MASTERCARD'
      : labelRaw === 'AMERICAN EXPRESS'
      ? 'AMEX'
      : labelRaw;

    const gradient =
      labelRaw === 'VISA'
        ? 'linear-gradient(135deg, #2a2a2a, #4e4e4e)'
        : labelRaw.includes('MASTERCARD')
        ? 'linear-gradient(135deg, #ea4335, #fbbc05)'
        : labelRaw.includes('AM EX') || labelRaw.includes('AMEX')
        ? 'linear-gradient(135deg, #016fd0, #17a0f3)'
        : labelRaw.includes('DISCOVER')
        ? 'linear-gradient(135deg, #ff7a00, #ffb266)'
        : 'linear-gradient(135deg, #343a40, #6c757d)';

    return (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 72,
          minWidth: 72,
          height: 24,
          borderRadius: 12,
          padding: '0 8px',
          color: '#fff',
          fontWeight: 700,
          fontSize: 12,
          background: gradient,
          lineHeight: 1,
        }}
      >
        {label}
      </span>
    );
  };

  // Pick a default: explicit defaultPaymentMethodId, otherwise first card
  useEffect(() => {
    if (!availableCards.length) {
      setSelectedCardId(null);
      return;
    }
    const preferred =
      defaultPaymentMethodId &&
      availableCards.find((c) => String(c.id) === String(defaultPaymentMethodId));
    setSelectedCardId(String((preferred || availableCards[0]).id));
  }, [availableCards, defaultPaymentMethodId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedCardId) {
      setMessage('Please select a card or add a new one.');
      return;
    }

    setMessage('');
    setIsLoading(true);

    try {
      let submitted = null;

      const hasPlan = !!(
        orderInfo?.selectedPlan &&
        orderInfo.selectedPlan.details &&
        orderInfo.selectedPlan.details.ext_product
      );

      if (hasPlan) {
        // Use existing card to create subscription
        submitted = await subService.createSubscription(
          targetUserId,
          selectedCardId,
          orderInfo,
          scriptId
        );
      } else if (invoice?.id) {
        // One-off invoice payment path
        submitted = await paymentService.payInvoice(invoice.id, selectedCardId);
      } else {
        setMessage('Card is currently unavailable. Please try again.');
      }

      if (!submitted) {
        setMessage('An unexpected error occurred saving your subscription.');
      } else if (nextStep) {
        nextStep(4);
      } else {
        window.location.reload();
      }
    } catch (err) {
      setMessage(err?.isDecline ? err.message : 'An unexpected error occurred.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleNewCardClick = () => setNewCard(true);

  const expiryText = (m, y) => (m && y ? `Expires ${String(m).padStart(2, '0')}/${y}` : '');

  return (
    <form onSubmit={handleSubmit} className="fullWidthContainer">
      <div className="fullWidthContainer" style={{ marginBottom: 16 }}>
        <span className="ThreePortionFlex bold" style={{ display: 'block', marginBottom: 8 }}>
          Use an existing card:
        </span>

        {/* Radio list */}
        <div className="TwoPortionFlex" role="radiogroup" aria-label="Saved cards">
          {availableCards.length === 0 ? (
            <div className="MinSmall" style={{ color: '#666' }}>
              You don’t have any saved cards yet.
            </div>
          ) : (
            availableCards.map((card) => {
              const idStr = String(card.id);
              return (
                <label
                  key={card.id}
                  className="cardOptionRow"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '10px 12px',
                    border: '1px solid #ddd',
                    borderRadius: 8,
                    marginBottom: 8,
                    cursor: 'pointer',
                    userSelect: 'none',
                  }}
                >
                  <input
                    type="radio"
                    name="saved-card"
                    value={idStr}
                    checked={selectedCardId === idStr}
                    onChange={() => setSelectedCardId(idStr)}
                    style={{ margin: 0 }}
                    aria-labelledby={`card-label-${idStr}`}
                  />

                  <BrandBadge brand={card.brand} />

                  {/* Masked number */}
                  <span id={`card-label-${idStr}`} style={{ fontWeight: 600 }}>
                    •••• {card.last4}
                  </span>

                  {/* Expiry on the right */}
                  <span className="MinSmall" style={{ color: '#666', marginLeft: 'auto' }}>
                    {expiryText(card.exp_month, card.exp_year)}
                  </span>
                </label>
              );
            })
          )}
        </div>
      </div>

      <div className="fullWidthContainer" style={{ paddingTop: 24, display: 'flex', gap: 12 }}>
        {/* New Card button */}
        {!isLoading ? (
          <button
            type="button"
            className="TwoPortionFlex buttonCloneLight"
            style={{ margin: 0 }}
            onClick={handleNewCardClick}
          >
            New Card
          </button>
        ) : (
          <span className="TwoPortionFlex" />
        )}

        <span className="OnePortionFlex" />

        {/* Pay Now button */}
        <button
          type="submit"
          className="TwoPortionFlex buttonClone"
          disabled={isLoading || !selectedCardId}
          id="finalize_payment_button_2"
          style={{ margin: 0 }}
        >
          <span id="button-text">{isLoading ? <LoadingSpinner /> : 'Pay now'}</span>
        </button>
      </div>

      {message ? (
        <div className="fullWidthContainer errorText" style={{ marginTop: 12 }}>
          {message}
        </div>
      ) : null}
    </form>
  );
};

export default CheckoutForm;
